import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Badge } from '@/components/ui/badge';
import { Bot, BookOpen, ArrowRight, CheckCircle2 } from 'lucide-react';

const steps = [
  {
    step: '01',
    title: 'Setup Your AI Agent',
    description: 'Configure your WhatsApp AI chatbot in minutes. Choose your agent\'s personality, set response guidelines, and connect to your WhatsApp number — no Business API required.',
    icon: Bot,
    features: ['Name your agent', 'Set personality & tone', 'Connect WhatsApp number'],
    color: 'primary',
  },
  {
    step: '02',
    title: 'Add Your Knowledge',
    description: 'Upload your business information, FAQs, product catalogs, and pricing. Your AI agent learns everything instantly and starts answering customers with precision.',
    icon: BookOpen,
    features: ['Upload documents & FAQs', 'Add product catalogs', 'Update anytime'],
    color: 'accent',
  },
];

export const HowItWorks = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <section id="how-it-works" ref={ref} className="py-24 lg:py-32 relative">
      {/* Background accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-primary/[0.03] blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 lg:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="premium" className="mb-4">
              Simple Setup
            </Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight"
          >
            Get Started in{' '}
            <span className="gradient-text-primary">2 Easy Steps</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed"
          >
            No complex integrations. No technical expertise needed. Just two simple steps to launch your AI-powered WhatsApp agent.
          </motion.p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {steps.map((step, i) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + i * 0.2 }}
              className="group"
            >
              <div className="glass-card rounded-2xl p-8 lg:p-10 h-full flex flex-col hover:border-primary/20 transition-all duration-500 group-hover:shadow-glow-primary/50">
                {/* Step indicator */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    step.color === 'primary' ? 'bg-primary/15' : 'bg-accent/15'
                  }`}>
                    <step.icon size={22} className={step.color === 'primary' ? 'text-primary' : 'text-accent'} />
                  </div>
                  <div>
                    <span className={`text-xs font-bold uppercase tracking-widest ${
                      step.color === 'primary' ? 'text-primary' : 'text-accent'
                    } font-heading`}>
                      Step {step.step}
                    </span>
                  </div>
                </div>

                <h3 className="font-heading text-xl lg:text-2xl font-semibold text-foreground mb-3">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-6 font-body">
                  {step.description}
                </p>

                {/* Feature list */}
                <div className="mt-auto space-y-3">
                  {step.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2.5">
                      <CheckCircle2 size={16} className={step.color === 'primary' ? 'text-primary' : 'text-accent'} />
                      <span className="text-sm text-secondary-foreground font-body">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Arrow between steps (desktop) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isVisible ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="hidden md:flex justify-center mt-10"
        >
          <div className="flex items-center gap-3 text-muted-foreground">
            <span className="text-sm font-body">That's it — your AI agent is live!</span>
            <ArrowRight size={16} className="text-primary" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
